import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

import skBg from '../images/sk_bg.png';

// Light mode background image URL
const lightModeBgUrl = 'https://images.unsplash.com/photo-1491002052546-bf38f186af56?q=80&w=2200&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D';

/**
 * StudentLayout Component
 * 
 * Wrapper component for all student-facing pages that provides:
 * - Consistent background color across the student interface
 * - Container for future shared student page logic
 * - Dynamic margin adjustment based on sidebar collapse state
 * 
 * This centralizes styling that was previously duplicated across
 * multiple student components (Courses, Profile, Dashboard, Lesson, etc.)
 */
const StudentLayout = ({ children }) => {
  const location = useLocation();
  const { themeColors, isDark } = useTheme();
  const isLessonPage = location.pathname.includes('/level/') && location.pathname.includes('/lesson/');
  const isWritersRoom = location.pathname === '/writers-room';
  
  const [sidebarMargin, setSidebarMargin] = useState(() => {
    // Check sidebar collapse state from localStorage on mount
    const saved = localStorage.getItem('sidebarCollapsed');
    const isCollapsed = saved ? JSON.parse(saved) : false;
    return isCollapsed ? 'md:ml-16' : 'md:ml-64';
  });

  useEffect(() => {
    // Check sidebar collapse state from localStorage
    const checkSidebarState = () => {
      const saved = localStorage.getItem('sidebarCollapsed');
      const isCollapsed = saved ? JSON.parse(saved) : false;
      setSidebarMargin(isCollapsed ? 'md:ml-16' : 'md:ml-64');
    };

    // Listen for custom event (when sidebar state changes)
    const handleCustomStorageChange = () => {
      checkSidebarState();
    };
    
    window.addEventListener('sidebarStateChanged', handleCustomStorageChange);

    return () => {
      window.removeEventListener('sidebarStateChanged', handleCustomStorageChange);
    };
  }, []);

  // WritersRoom uses fixed positioning and manages its own layout, so skip margin
  if (isWritersRoom) {
    return (
      <>
        {/* Skip link for keyboard users */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[200] focus:px-4 focus:py-2 focus:bg-white focus:text-blue-700 focus:rounded focus:shadow-lg focus:font-semibold"
        >
          Skip to main content
        </a>
        <main id="main-content">{children}</main>
      </>
    );
  }

  return (
    <>
      {/* Skip link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[200] focus:px-4 focus:py-2 focus:bg-white focus:text-blue-700 focus:rounded focus:shadow-lg focus:font-semibold"
      >
        Skip to main content
      </a>
      <div 
        className={`relative min-h-screen ${sidebarMargin} transition-all duration-300 ease-in-out`}
        style={{
          ...(isLessonPage ? {} : {
            backgroundImage: isDark ? `url(${skBg})` : `url(${lightModeBgUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
          }),
          backgroundColor: themeColors.background,
        }}
      >
        {/* Gradient overlay for better card contrast */}
        {!isLessonPage && isDark && (
          <div 
            className="absolute inset-0 z-0"
            style={{
              background: 'linear-gradient(to bottom, rgba(25, 25, 25, 0.85) 0%, rgba(19, 19, 19, 0.95) 100%)'
            }}
          />
        )}
        {!isLessonPage && !isDark && (
          <div 
            className="absolute inset-0 z-0"
            style={{
              background: 'linear-gradient(to bottom, rgba(250, 250, 250, 0.85) 0%, rgba(245, 245, 245, 0.95) 100%)'
            }}
          />
        )}
        {/* Main page content */}
        <main id="main-content" className="relative z-10">
          {children}
        </main>
      </div>
    </>
  );
};

export default StudentLayout;